// External imports
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// Internal imports
import { reviewsService } from "../services/reviews.service";
import {
  CreateReviewRequest,
  UpdateReviewRequest,
  Review,
} from "@/types/reviews.types";

/**
 * Hook to get user's review for a specific restaurant
 */
export const useUserRestaurantReview = (restaurantId: string) => {
  return useQuery({
    queryKey: ["user-restaurant-review", restaurantId],
    queryFn: () => reviewsService.getUserRestaurantReview(restaurantId),
    enabled: !!restaurantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to check if user has reviewed a restaurant
 */
export const useCheckUserReview = (restaurantId: string) => {
  return useQuery({
    queryKey: ["check-user-review", restaurantId],
    queryFn: () => reviewsService.checkUserReview(restaurantId),
    enabled: !!restaurantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to create a new review
 */
export const useCreateReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateReviewRequest) => {
      return await reviewsService.createReview(data);
    },
    onSuccess: (review: Review) => {
      // Invalidate and refetch review-related queries
      queryClient.invalidateQueries({
        queryKey: ["user-restaurant-review", review.restaurantId]
      });
      queryClient.invalidateQueries({
        queryKey: ["check-user-review", review.restaurantId]
      });
      queryClient.invalidateQueries({
        queryKey: ["restaurant-reviews", review.restaurantId]
      });
      queryClient.invalidateQueries({
        queryKey: ["restaurant-review-stats", review.restaurantId]
      });
      queryClient.invalidateQueries({
        queryKey: ["user-review-stats"]
      });

      toast.success("Review added successfully!");
    },
    onError: (error: Error) => {
      console.error("Failed to create review:", error);

      if (error.message.includes("already reviewed")) {
        toast.error("You have already reviewed this restaurant");
      } else {
        toast.error("Failed to add review. Please try again.");
      }
    },
  });
};

/**
 * Hook to update an existing review
 */
export const useUpdateReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reviewId, data }: { reviewId: string; data: UpdateReviewRequest }) => {
      return await reviewsService.updateReview(reviewId, data);
    },
    onSuccess: (review: Review) => {
      // Invalidate and refetch review-related queries
      queryClient.invalidateQueries({
        queryKey: ["user-restaurant-review", review.restaurantId]
      });
      queryClient.invalidateQueries({
        queryKey: ["check-user-review", review.restaurantId]
      });
      queryClient.invalidateQueries({
        queryKey: ["restaurant-reviews", review.restaurantId]
      });
      queryClient.invalidateQueries({
        queryKey: ["restaurant-review-stats", review.restaurantId]
      });
      queryClient.invalidateQueries({
        queryKey: ["user-review-stats"]
      });

      toast.success("Review updated successfully!");
    },
    onError: (error: Error) => {
      console.error("Failed to update review:", error);
      toast.error("Failed to update review. Please try again.");
    },
  });
};

/**
 * Hook to delete a review
 */
export const useDeleteReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reviewId, restaurantId }: { reviewId: string; restaurantId: string }) => {
      await reviewsService.deleteReview(reviewId);
      return { reviewId, restaurantId };
    },
    onSuccess: ({ restaurantId }) => {
      // Invalidate and refetch review-related queries
      queryClient.invalidateQueries({
        queryKey: ["user-restaurant-review", restaurantId]
      });
      queryClient.invalidateQueries({
        queryKey: ["check-user-review", restaurantId]
      });
      queryClient.invalidateQueries({
        queryKey: ["restaurant-reviews", restaurantId]
      });
      queryClient.invalidateQueries({
        queryKey: ["restaurant-review-stats", restaurantId]
      });
      queryClient.invalidateQueries({
        queryKey: ["user-review-stats"]
      });

      toast.success("Review deleted successfully!");
    },
    onError: (error: Error) => {
      console.error("Failed to delete review:", error);
      toast.error("Failed to delete review. Please try again.");
    },
  });
};

/**
 * Hook to get all reviews for a restaurant
 */
export const useRestaurantReviews = (restaurantId: string) => {
  return useQuery({
    queryKey: ["restaurant-reviews", restaurantId],
    queryFn: () => reviewsService.getRestaurantReviews(restaurantId),
    enabled: !!restaurantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to get review statistics for a restaurant
 */
export const useRestaurantReviewStats = (restaurantId: string) => {
  return useQuery({
    queryKey: ["restaurant-review-stats", restaurantId],
    queryFn: () => reviewsService.getRestaurantReviewStats(restaurantId),
    enabled: !!restaurantId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to get user's review statistics
 */
export const useUserReviewStats = () => {
  return useQuery({
    queryKey: ["user-review-stats"],
    queryFn: () => reviewsService.getUserReviewStats(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to get recent reviews
 */
export const useRecentReviews = (limit: number = 10) => {
  return useQuery({
    queryKey: ["recent-reviews", limit],
    queryFn: () => reviewsService.getRecentReviews(limit),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Combined hook for managing restaurant reviews
 * Provides convenient access to all review operations for a restaurant
 */
export const useRestaurantReviewManager = (restaurantId: string) => {
  const userReview = useUserRestaurantReview(restaurantId);
  const reviewStats = useRestaurantReviewStats(restaurantId);
  const createReview = useCreateReview();
  const updateReview = useUpdateReview();
  const deleteReview = useDeleteReview();

  return {
    // Data
    userReview: userReview.data,
    hasUserReview: !!userReview.data,
    reviewStats: reviewStats.data,

    // Loading states
    isLoadingUserReview: userReview.isLoading,
    isLoadingStats: reviewStats.isLoading,
    isCreating: createReview.isPending,
    isUpdating: updateReview.isPending,
    isDeleting: deleteReview.isPending,

    // Actions
    createReview: (data: CreateReviewRequest) => createReview.mutateAsync(data),
    updateReview: (data: UpdateReviewRequest) =>
      userReview.data ? updateReview.mutateAsync({ reviewId: userReview.data.id, data }) : Promise.reject("No review to update"),
    deleteReview: () =>
      userReview.data ? deleteReview.mutateAsync({ reviewId: userReview.data.id, restaurantId }) : Promise.reject("No review to delete"),

    // Utilities
    isLoading: userReview.isLoading || reviewStats.isLoading,
    isMutating: createReview.isPending || updateReview.isPending || deleteReview.isPending,

    // Refetch functions
    refetchUserReview: userReview.refetch,
    refetchStats: reviewStats.refetch,
  };
};